/* eslint-disable unicorn/no-null */

import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import {
    ActionIcon,
    Box,
    Button,
    Code,
    Group,
    Loader,
    Tabs,
    Text,
    Tooltip,
    UnstyledButton
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useIntersection, useMergedRef } from '@mantine/hooks';
import { ChevronLeft, ChevronRight, Lock, Save, X } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import useDesignerFilesStore from '../DesignerFiles.store';
import {
    FileSaveConflictError,
    useFileContentQuery,
    useSaveFileMutation
} from '../Files.queries';
import FileEditor from '../FileEditor';
import FileSaveConflictModal from '../FileSaveConflictModal';
import styles from './EditorArea.module.css';

interface EditorAreaProps {
    isReadOnly: boolean;
}

const getOutOfViewSide = (entry: IntersectionObserverEntry | null) => {
    if (!entry || entry.isIntersecting || !entry.rootBounds) {
        return null;
    }

    return entry.boundingClientRect.right <= entry.rootBounds.left
        ? 'left'
        : 'right';
};

const EditorArea: FC<EditorAreaProps> = ({ isReadOnly }) => {
    const {
        openFiles,
        openFileOrder,
        activeFilePath,
        closeFile,
        markDirty,
        setActiveFile,
        setFileContent,
        openFile,
        setBaseGeneration,
        selectedVersionId
    } = useDesignerFilesStore(
        useShallow((state) => ({
            openFiles: state.openFiles,
            openFileOrder: state.openFileOrder,
            activeFilePath: state.activeFilePath,
            selectedVersionId: state.selectedVersionId,
            closeFile: state.closeFile,
            markDirty: state.markDirty,
            setActiveFile: state.setActiveFile,
            setFileContent: state.setFileContent,
            openFile: state.openFile,
            setBaseGeneration: state.setBaseGeneration
        }))
    );

    const saveFileMutation = useSaveFileMutation();

    const activeTabRef = useRef<HTMLButtonElement | null>(null);
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const { ref: intersectionRef, entry: activeTabEntry } =
        useIntersection<HTMLButtonElement>({
            root: scrollContainer,
            threshold: 0
        });
    const mergedActiveTabRef = useMergedRef(activeTabRef, intersectionRef);
    const outOfViewSide = getOutOfViewSide(activeTabEntry);

    const prevActiveRef = useRef<string | null>(null);
    useEffect(() => {
        if (
            prevActiveRef.current !== activeFilePath &&
            activeFilePath &&
            activeTabRef.current
        ) {
            activeTabRef.current.scrollIntoView({
                inline: 'nearest',
                behavior: 'smooth',
                block: 'nearest'
            });
        }
        prevActiveRef.current = activeFilePath;
    }, [activeFilePath]);

    const handleRecenterActive = () => {
        activeTabRef.current?.scrollIntoView({
            inline: 'center',
            behavior: 'smooth',
            block: 'nearest'
        });
    };

    const activeFileName = activeFilePath
        ? (activeFilePath.split('/').pop() ?? activeFilePath)
        : '';

    const fileContentQuery = useFileContentQuery(
        selectedVersionId,
        activeFilePath
    );

    useEffect(() => {
        const result = fileContentQuery.data;
        if (!result || !activeFilePath) {
            return;
        }
        if (result.path !== activeFilePath) {
            return;
        }
        if (openFiles[activeFilePath] === undefined) {
            openFile(activeFilePath, result.content, result.generation);
            return;
        }

        const stored = openFiles[activeFilePath];
        if (!stored.dirty && stored.content !== result.content) {
            setFileContent(activeFilePath, result.content);
        }

        if (!stored.dirty && stored.baseGeneration !== result.generation) {
            setBaseGeneration(activeFilePath, result.generation);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileContentQuery.data, activeFilePath]);

    const handleTabChange = (path: string | null) => {
        setActiveFile(path);
    };

    const handleCloseTab = (path: string) => {
        const file = openFiles[path];
        if (file?.dirty) {
            const filename = path.split('/').pop() ?? path;
            const id = modals.open({
                title: 'Unsaved changes',
                centered: true,
                children: (
                    <Box className="flex flex-col gap-4">
                        <Text size="sm">
                            You have unsaved changes in <Code>{filename}</Code>.
                            What would you like to do?
                        </Text>
                        <Group gap="sm" justify="flex-end">
                            <Button
                                variant="default"
                                onClick={() => {
                                    modals.close(id);
                                }}
                            >
                                Keep Editing
                            </Button>
                            <Button
                                color="red"
                                variant="outline"
                                onClick={() => {
                                    modals.close(id);
                                    closeFile(path);
                                }}
                            >
                                Discard
                            </Button>
                            <Button
                                onClick={() => {
                                    modals.close(id);

                                    const latest =
                                        useDesignerFilesStore.getState()
                                            .openFiles[path];
                                    const latestContent = latest?.content ?? '';
                                    const latestGeneration =
                                        latest?.baseGeneration ?? null;
                                    saveFileMutation.mutate(
                                        {
                                            path,
                                            content: latestContent,
                                            baseGeneration: latestGeneration
                                        },
                                        {
                                            onSuccess: () => {
                                                closeFile(path);
                                            },
                                            onError: (error) => {
                                                if (
                                                    error instanceof
                                                    FileSaveConflictError
                                                ) {
                                                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                                                    showSaveConflictModal(
                                                        error.path,
                                                        latestContent
                                                    );
                                                }
                                            }
                                        }
                                    );
                                }}
                            >
                                Save
                            </Button>
                        </Group>
                    </Box>
                )
            });
        } else {
            closeFile(path);
        }
    };

    const handleEditorChange = (value: string | undefined) => {
        const state = useDesignerFilesStore.getState();
        const path = state.activeFilePath;
        if (!path) {
            return;
        }
        const stored = state.openFiles[path];
        const next = value ?? '';

        if (stored?.content === next) {
            return;
        }
        setFileContent(path, next);
        markDirty(path, true);
    };

    const reloadFromRemote = (path: string) => {
        markDirty(path, false);

        setBaseGeneration(path, null);
        if (selectedVersionId) {
            void fileContentQuery.refetch();
        }
    };

    const showSaveConflictModal = (path: string, content: string) => {
        const filename = path.split('/').pop() ?? path;
        const id = modals.open({
            title: 'File changed elsewhere',
            centered: true,
            children: (
                <FileSaveConflictModal
                    filename={filename}
                    onCancel={() => {
                        modals.close(id);
                    }}
                    onReload={() => {
                        modals.close(id);
                        reloadFromRemote(path);
                    }}
                    onSaveAnyway={() => {
                        modals.close(id);

                        saveFileMutation.mutate({
                            path,
                            content,
                            force: true
                        });
                    }}
                />
            )
        });
    };

    const handleSave = () => {
        if (!activeFilePath || !openFiles[activeFilePath]) {
            return;
        }
        const file = openFiles[activeFilePath];
        saveFileMutation.mutate(
            {
                path: activeFilePath,
                content: file.content,
                baseGeneration: file.baseGeneration
            },
            {
                onSuccess: () => {
                    markDirty(activeFilePath, false);
                },
                onError: (error) => {
                    if (error instanceof FileSaveConflictError) {
                        showSaveConflictModal(error.path, file.content);
                    }
                }
            }
        );
    };

    if (openFileOrder.length === 0) {
        return (
            <Box className="flex h-full flex-col">
                <Box className="h-10 shrink-0 border-b border-gray-3" />
                <Box className="flex flex-1 items-center justify-center">
                    <Text c="dimmed" size="sm">
                        Select a file from the tree to start editing.
                    </Text>
                </Box>
            </Box>
        );
    }

    const activeIsDirty = activeFilePath
        ? (openFiles[activeFilePath]?.dirty ?? false)
        : false;

    return (
        <Box className="flex h-full flex-col">
            <Tabs
                className="flex min-h-0 flex-1 flex-col"
                classNames={{
                    list: `h-full w-full flex-nowrap overflow-x-auto overflow-y-hidden before:hidden ${styles.tabsList}`
                }}
                keepMounted={false}
                value={activeFilePath}
                onChange={handleTabChange}
            >
                <Box className="flex h-10 shrink-0 items-center border-b border-gray-3">
                    <Box className="relative h-full min-w-0 flex-1">
                        <Tabs.List ref={setScrollContainer}>
                            {openFileOrder.map((path) => {
                                const filename = path.split('/').pop() ?? path;
                                const isDirty = openFiles[path]?.dirty ?? false;
                                const isActive = path === activeFilePath;
                                return (
                                    <Tabs.Tab
                                        className="shrink-0"
                                        key={path}
                                        ref={
                                            isActive ? mergedActiveTabRef : null
                                        }
                                        rightSection={
                                            <Group gap={4} wrap="nowrap">
                                                {isDirty ? (
                                                    <Text
                                                        className="text-yellow-6"
                                                        component="span"
                                                        title="Unsaved changes"
                                                    >
                                                        ●
                                                    </Text>
                                                ) : null}
                                                <ActionIcon
                                                    aria-label={`Close ${filename}`}
                                                    size={14}
                                                    variant="subtle"
                                                    onClick={(event_) => {
                                                        event_.stopPropagation();
                                                        handleCloseTab(path);
                                                    }}
                                                >
                                                    <X size={12} />
                                                </ActionIcon>
                                            </Group>
                                        }
                                        value={path}
                                    >
                                        {filename}
                                    </Tabs.Tab>
                                );
                            })}
                        </Tabs.List>

                        {outOfViewSide === 'left' ? (
                            <UnstyledButton
                                className="absolute inset-y-0 left-0 z-20 flex items-center gap-1 border-r border-gray-3 bg-gray-1 px-2 text-xs font-medium text-gray-7 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] hover:bg-gray-2"
                                title={`Scroll to active tab (${activeFileName})`}
                                onClick={handleRecenterActive}
                            >
                                <ChevronLeft size={14} />
                                <Text
                                    className="max-w-[10rem] truncate"
                                    component="span"
                                    size="xs"
                                >
                                    {activeFileName}
                                </Text>
                            </UnstyledButton>
                        ) : null}
                        {outOfViewSide === 'right' ? (
                            <UnstyledButton
                                className="absolute inset-y-0 right-0 z-20 flex items-center gap-1 border-l border-gray-3 bg-gray-1 px-2 text-xs font-medium text-gray-7 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.08)] hover:bg-gray-2"
                                title={`Scroll to active tab (${activeFileName})`}
                                onClick={handleRecenterActive}
                            >
                                <Text
                                    className="max-w-[10rem] truncate"
                                    component="span"
                                    size="xs"
                                >
                                    {activeFileName}
                                </Text>
                                <ChevronRight size={14} />
                            </UnstyledButton>
                        ) : null}
                    </Box>

                    {activeFilePath ? (
                        <Box className="flex shrink-0 items-center pr-3">
                            {isReadOnly ? (
                                <Tooltip
                                    label="This version is published and cannot be edited"
                                    position="left"
                                >
                                    <ActionIcon
                                        aria-label="Read-only"
                                        color="gray"
                                        size="sm"
                                        variant="subtle"
                                    >
                                        <Lock size={14} />
                                    </ActionIcon>
                                </Tooltip>
                            ) : (
                                <Tooltip label="Save (⌘S)" position="left">
                                    <ActionIcon
                                        aria-label="Save file"
                                        disabled={!activeIsDirty}
                                        loading={saveFileMutation.isPending}
                                        size="sm"
                                        variant="subtle"
                                        onClick={handleSave}
                                    >
                                        <Save size={14} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Box>
                    ) : null}
                </Box>

                {activeFilePath ? (
                    <Tabs.Panel
                        className="min-h-0 flex-1"
                        value={activeFilePath}
                    >
                        {openFiles[activeFilePath] === undefined ||
                        !selectedVersionId ? (
                            <Box className="flex h-full items-center justify-center">
                                <Loader size="sm" />
                            </Box>
                        ) : (
                            <FileEditor
                                filePath={activeFilePath}
                                readOnly={isReadOnly}
                                value={openFiles[activeFilePath].content}
                                versionId={selectedVersionId}
                                onChange={handleEditorChange}
                                onSave={handleSave}
                            />
                        )}
                    </Tabs.Panel>
                ) : null}
            </Tabs>
        </Box>
    );
};

export default EditorArea;
